import type { AttemptGateView } from "@remi/contracts/services/task.service";
import type { RealtimeAttemptAbortReason } from "@remi/task/attempt-abort-reason";
import { isOneOf } from "@remi/ts-utils";
import type { AttemptPhase, CacheMessageForType, CacheMessageType } from "@remi/types/socket/message";

export const GENERIC_ATTEMPT_ERROR = "Something went wrong recording that attempt. Refreshing this task.";

export type AttemptTrackerPhase = "pending" | "delayed" | "committed" | "settled";

// Transitions compare ranks, so a duplicate publish (Inngest step replay after a lost ack) or an
// out-of-order one (committed and settled publish from different invocations) is mechanically a no-op.
export const PHASE_RANK = {
  pending: 0,
  delayed: 1,
  committed: 2,
  settled: 3,
} as const satisfies Record<AttemptTrackerPhase, number>;

type ProjectTasksMessageClass =
  | { kind: "ordinary" | "committed" | "failed" | "settled" }
  | { kind: "aborted"; reason: string };

const WIRE_ATTEMPT_PHASES = ["committed", "failed"] as const satisfies readonly AttemptPhase[];

export const classifyProjectTasksMessage = (
  message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>,
): ProjectTasksMessageClass => {
  if (message.attemptDedupeKey === undefined) {
    return { kind: "ordinary" };
  }
  // Must stay ahead of both phase branches: an abort publish carries a reason and NO phase, which the
  // phase-less check below would otherwise classify as a settle.
  if (message.attemptAbortReason !== undefined) {
    return { kind: "aborted", reason: message.attemptAbortReason };
  }
  if (message.attemptPhase === undefined) {
    return { kind: "settled" };
  }
  // Unrecognized phases degrade to settled rather than failing closed — the wire field is a loose
  // string so a future phase from a newer server does not strand this client.
  return { kind: isOneOf(message.attemptPhase, WIRE_ATTEMPT_PHASES) ? message.attemptPhase : "settled" };
};

interface AbortDisposition {
  rollback: boolean;
  message: string | null;
}

const ABORT_DISPOSITIONS = {
  TASK_NOT_FOUND: { rollback: true, message: "This task changed — refreshing." },
  TASK_NOT_OPEN: { rollback: true, message: "This task changed — refreshing." },
  STREAK_GATE_UNMET: { rollback: true, message: GENERIC_ATTEMPT_ERROR },
  RACING_DUPLICATE_UNCOMMITTED: { rollback: false, message: null },
  NOT_PILOT_TYPE: { rollback: true, message: GENERIC_ATTEMPT_ERROR },
  REASON_NOT_OFFERED_FOR_CATEGORY: {
    rollback: true,
    message: "This outcome isn't available on this project's category.",
  },
  REASON_NOT_OFFERED_FOR_TASK_TYPE: {
    rollback: true,
    message: "This outcome isn't available on this task.",
  },
} as const satisfies Record<RealtimeAttemptAbortReason, AbortDisposition>;

export const ABORT_DISPOSITION_BY_REASON = new Map<string, AbortDisposition>(Object.entries(ABORT_DISPOSITIONS));

export const streakGateCopy = (gate: AttemptGateView | null | undefined): string =>
  gate?.abortCopy ?? GENERIC_ATTEMPT_ERROR;
